// Oylan stand: daily lessons on top of OATutor.
//
// Upstream OATutor keeps mastery and the list of solved problems forever, so a topic
// could be played exactly once: a mastered topic reopened as "completed" with no
// problems, a failed one stayed "exhausted" for good. Here every lesson starts over on
// a new calendar day, problems the child has not seen yet come first, and a number
// answer written with a word ("3 шара") is read as the number.

export const SEEN_STORAGE_KEY = "oylan-seen";
export const SESSION_DAY_STORAGE_KEY = (lessonId) => `oylan-session-day-${lessonId}`;

// Local calendar day, so a new lesson starts after midnight in Kazakhstan, not in UTC.
export function todayKey(now = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function isNewSession(savedDay, today) {
    return savedDay !== today;
}

// OATutor shares the parameter objects of defaultBKTParams.json with the live model and
// updates them in place, so the starting values must be copied before the first answer.
export function snapshotParams(params) {
    return JSON.parse(JSON.stringify(params));
}

// Puts mastery of the lesson's skills back to the starting value, in place: Problem.js
// holds references to these objects and updates them directly.
export function resetMastery(bktParams, skills, initialParams) {
    for (const skill of skills) {
        if (bktParams[skill] && initialParams[skill]) {
            bktParams[skill].probMastery = initialParams[skill].probMastery;
        }
    }
}

// Most recently seen problem goes to the end of the list.
export function markSeen(seenOrder, problemId) {
    return [...seenOrder.filter((id) => id !== problemId), problemId];
}

// Same rule as OATutor defaultHeuristic (lowest mastery first, skip solved in this lesson
// and already mastered). Among equals the task closest to the child's level wins (levelRank
// from levels.js, lower is better), then the problem seen longest ago, unseen first.
export function pickProblem(problems, completedProbs, seenOrder, threshold, random = Math.random, levelRank = () => 0) {
    const rank = (id) => seenOrder.indexOf(id); // -1 = never seen
    let best = [];
    for (const problem of problems) {
        if (completedProbs.has(problem.id) || problem.probMastery == null || problem.probMastery >= threshold) {
            continue;
        }
        if (best.length === 0 || problem.probMastery < best[0].probMastery) {
            best = [problem];
        } else if (problem.probMastery === best[0].probMastery) {
            best.push(problem);
        }
    }
    if (best.length === 0) return undefined;
    const closest = Math.min(...best.map(levelRank));
    best = best.filter((p) => levelRank(p) === closest);
    const oldest = Math.min(...best.map((p) => rank(p.id)));
    const candidates = best.filter((p) => rank(p.id) === oldest);
    return candidates[Math.floor(random() * candidates.length)];
}

// Only count nouns used by Oylan tasks may follow a number. An arbitrary word
// could negate or qualify the answer ("5 ложно") and must not be discarded.
const UNIT_WORDS = new Set(`
    шар шара шаров взвешивание взвешивания взвешиваний
    число числа чисел наряд наряда нарядов рукопожатие рукопожатия рукопожатий
    способ способа способов конфета конфеты конфет день дня дней
    яблоко яблока яблок год года лет марка марки марок
    килограмм килограмма килограммов минута минуты минут носок носка носков
    вариант варианта вариантов наклейка наклейки наклеек час часа часов
    страница страницы страниц тенге ступенька ступеньки ступенек
    паук паука пауков двойка двойки двоек пара пары пар
    флаг флага флагов орех ореха орехов карандаш карандаша карандашей
    монета монеты монет книга книги книг код кода кодов
    маршрут маршрута маршрутов порядок порядка порядков
`.trim().split(/\s+/));

// For a whole-number answer, accept a bare number, "ответ 3", or a number with
// one recognized unit word ("3 шара"). Other text stays intact for strict checking.
export function numberFromWords(attempt, expectedAnswers) {
    if (!expectedAnswers.every((a) => /^\d+$/.test(String(a).trim()))) return attempt;
    const match = String(attempt).trim().match(/^(?:ответ\s*:?\s*)?(\d+)(?:\s+([а-яё]+))?$/i);
    if (!match) return attempt;

    if (match[2] && !UNIT_WORDS.has(match[2].toLowerCase())) return attempt;
    return match[1];
}
